import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Cartao,
  CelulaDeTotal,
  Modal,
  Selo,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  type ColunaTabela,
} from '../componentes/ui';
import { requisicaoAutenticada } from '../servicos/http';
import { formatarCadIcms, formatarCnpj, formatarCompetencia } from '../servicos/formato';
import type { LinhaSped } from '../servicos/operacao';
import { ErroDaApi } from './ErroDaApi';

/**
 * Uma competência calculada, na forma que a grade desenha.
 *
 * A prévia devolve todos os números; o envio de verdade devolve `LinhaSped`,
 * em que deságio, valor a pagar e benefício são `null` até o contrato existir.
 * A grade aceita a forma mais frouxa e mostra travessão onde falta número —
 * assim as duas telas (antes e depois de cadastrar) são a mesma tabela.
 */
interface LinhaDaGrade {
  readonly competencia: string;
  readonly totalSped: number;
  readonly percentSpedPercentual: number;
  readonly valueSped: number;
  readonly percentBeneficioPercentual: number | null;
  readonly total: number | null;
  readonly valueBeneficio: number | null;
}

interface Contribuinte {
  readonly razaoSocial: string;
  readonly cnpj: string;
  readonly cadIcms: string;
  readonly uf: string;
}

type Ocorrencias = {
  readonly avisos: readonly { arquivo: string; codigo: string; mensagem: string }[];
  readonly recusados: readonly { arquivo: string; motivo: string }[];
};

/** O cálculo dos arquivos, antes de existir cliente. */
interface ResultadoPrevia extends Ocorrencias {
  readonly contribuinte: Contribuinte | null;
  readonly linhas: readonly LinhaDaGrade[];
}

/** O que `POST /icms/spedfiles` devolve — as linhas gravadas, sem o contribuinte. */
interface ResultadoUpload extends Ocorrencias {
  readonly clienteId: string;
  readonly linhas: readonly LinhaSped[];
}

/**
 * Adicionar cliente pelo arquivo SPED.
 *
 * ## O cliente nasce do arquivo
 *
 * O EFD traz o CNPJ do contribuinte no registro 0000. A rota resolve a empresa
 * por ele (RN-181): se já existe, os cálculos entram nela; se não, ela é criada.
 * É por isso que aqui não se digita nada — o arquivo já sabe de quem é.
 *
 * ## O que aparece depois
 *
 * A **mesma grade da prévia**, agora com o que foi gravado: mês a mês, com
 * apurado, utilização, deságio, valor a pagar e benefício, e os totais. Antes o
 * pós-cadastro mostrava uma lista resumida (só apurado e crédito), e quem
 * queria conferir o cálculo completo precisava abrir o cliente — a tabela que
 * acabara de ver na prévia sumia no clique seguinte.
 *
 * O contribuinte vem da prévia, que ainda está em memória: o envio de verdade
 * não o devolve, e é o mesmo arquivo. Um arquivo recusado não derruba os
 * outros: cada um é lido por conta própria e a tela mostra os dois lados — o
 * que entrou e o que ficou de fora, com o motivo.
 *
 * ## Para o assessor, é o simulador do Recorrente
 *
 * Com `empresaDoAssessor`, a mesma tela atende o assessor: a prévia calcula o
 * crédito do contribuinte **sem gravar nada** — é a simulação que ele mostra
 * ao cliente — e "Cadastrar na minha carteira" cria o cliente vinculado à
 * assessoria dele (RN-621). Contribuinte que já é cliente de outra assessoria
 * é recusado pela API: quem move cliente de carteira é a operação.
 */
export function AdicionarPorSped(props: {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  /** A assessoria escolhida no login — o modo do assessor (ver acima). */
  readonly empresaDoAssessor?: string;
}): JSX.Element {
  const assessor = props.empresaDoAssessor;
  /** Sem cliente informado, quem não é admin diz em nome de que empresa envia. */
  const emNomeDe = assessor === undefined ? {} : { businessId: assessor };
  const navegar = useNavigate();
  const queryClient = useQueryClient();
  const [arquivos, setArquivos] = useState<readonly File[]>([]);

  const lerArquivos = async (): Promise<{ nome: string; conteudo: string }[]> =>
    Promise.all(arquivos.map(async (a) => ({ nome: a.name, conteudo: await a.text() })));

  /**
   * A prévia não grava nada.
   *
   * Antes o clique criava a empresa, o papel e os cálculos de uma vez — quem
   * operava só via o que tinha enviado depois de já estar cadastrado, e um
   * arquivo do contribuinte errado virava empresa nova para apagar à mão. A
   * conta é a mesma do envio de verdade, então o número da prévia é o número
   * que será gravado.
   */
  const previa = useMutation({
    mutationFn: async () =>
      requisicaoAutenticada<ResultadoPrevia>('/icms/spedfiles/previa', {
        metodo: 'POST',
        corpo: { arquivos: await lerArquivos(), ...emNomeDe },
      }),
  });

  const enviar = useMutation({
    mutationFn: async () => {
      const conteudos = await lerArquivos();

      return requisicaoAutenticada<ResultadoUpload>('/icms/spedfiles', {
        metodo: 'POST',
        corpo: { arquivos: conteudos, ...emNomeDe },
      });
    },
    onSuccess: async (resultado) => {
      if (assessor !== undefined) {
        // O envio já cadastra o cliente na assessoria (RN-621); a configuração
        // de papéis é da operação.
        void queryClient.invalidateQueries({ queryKey: ['participacao'] });
        return;
      }
      // O SPED cria a empresa, não o papel: sem isto ela não entra na carteira
      // de clientes (RN-015).
      await requisicaoAutenticada('/icms/settings', {
        metodo: 'POST',
        corpo: { businessId: resultado.clienteId, cliente: true },
      }).catch(() => undefined);

      void queryClient.invalidateQueries({ queryKey: ['carteira'] });
    },
  });

  const fechar = (): void => {
    previa.reset();
    enviar.reset();
    setArquivos([]);
    props.aoFechar();
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo={assessor === undefined ? 'Adicionar por arquivo SPED' : 'Simular pelo SPED'}
      largura="larga"
      rodape={
        enviar.isSuccess ? (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Fechar
            </Botao>
            <Botao
              onClick={() => {
                const id = enviar.data.clienteId;
                fechar();
                navegar(`/clientes/${id}`);
              }}
            >
              Abrir o cliente
            </Botao>
          </>
        ) : previa.isSuccess ? (
          <>
            <Botao variante="secundario" onClick={() => previa.reset()}>
              Voltar
            </Botao>
            <Botao
              onClick={() => enviar.mutate()}
              disabled={previa.data.linhas.length === 0}
              carregando={enviar.isPending}
            >
              {assessor === undefined ? 'Cadastrar cliente' : 'Cadastrar na minha carteira'}
            </Botao>
          </>
        ) : (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              onClick={() => previa.mutate()}
              disabled={arquivos.length === 0}
              carregando={previa.isPending}
            >
              Calcular {arquivos.length > 0 && `${arquivos.length} arquivo(s)`}
            </Botao>
          </>
        )
      }
    >
      {enviar.isSuccess ? (
        <div className="flex flex-col gap-4">
          <Aviso
            tom="positivo"
            titulo={`${enviar.data.linhas.length} competência(s) processada(s)`}
          >
            O contribuinte foi identificado pelo CNPJ do próprio arquivo e já está na carteira de
            clientes.
          </Aviso>

          <PreviaDosArquivos
            dados={{
              contribuinte: previa.data?.contribuinte ?? null,
              linhas: enviar.data.linhas,
              avisos: enviar.data.avisos,
              recusados: enviar.data.recusados,
            }}
            legenda="Cálculo por competência, gravado no cliente"
          />
        </div>
      ) : previa.isSuccess ? (
        <div className="flex flex-col gap-4">
          <PreviaDosArquivos dados={previa.data} />
          {/* O erro do cadastro acontece nesta etapa; mostrá-lo só na etapa
              de arquivos deixava o clique em "Cadastrar" sem resposta. */}
          {enviar.isError && <ErroDaApi titulo="Não foi possível cadastrar" erro={enviar.error} />}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {assessor === undefined ? (
            <Aviso tom="informacao" titulo="O arquivo diz de quem ele é">
              O CNPJ do contribuinte vem no registro 0000 do EFD. Se a empresa já existir, os
              cálculos entram nela; se não, ela é criada com os dados do arquivo.
            </Aviso>
          ) : (
            <Aviso tom="informacao" titulo="Calcular não cadastra nada">
              O crédito aparece mês a mês, antes de qualquer cadastro: é a simulação para mostrar ao
              cliente. Se ele quiser seguir, “Cadastrar na minha carteira” cria o cliente vinculado
              à sua assessoria.
            </Aviso>
          )}

          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-conteudo-primario">Arquivos SPED (.txt)</span>
            <input
              type="file"
              accept=".txt"
              multiple
              onChange={(e) => setArquivos(Array.from(e.target.files ?? []))}
              className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm"
            />
            <span className="text-xs text-conteudo-sutil">
              Um por competência, até 24 por envio.
            </span>
          </label>

          {arquivos.length > 0 && (
            <ul className="flex flex-wrap gap-1">
              {arquivos.map((a) => (
                <li key={a.name}>
                  <Selo tom="neutro" tamanho="pequeno">
                    {a.name}
                  </Selo>
                </li>
              ))}
            </ul>
          )}

          {previa.isError && <ErroDaApi titulo="Não foi possível calcular" erro={previa.error} />}
        </div>
      )}
    </Modal>
  );
}

/* ─── Prévia ──────────────────────────────────────────────────────────────── */

/**
 * O que os arquivos dizem, antes de virar cadastro.
 *
 * Mostra quem o SPED identificou e a grade mês a mês, nas mesmas colunas e na
 * mesma ordem do resto do sistema — valor a pagar antes do benefício. Quem
 * confere aqui está olhando exatamente o que será gravado.
 */
function PreviaDosArquivos(props: {
  readonly dados: ResultadoPrevia;
  readonly legenda?: string;
}): JSX.Element {
  const { contribuinte, linhas, recusados, avisos } = props.dados;

  // RN-075 — mês com apuração negativa não gera crédito, e não desconta do
  // total de quem gerou. Somá-lo faria a prévia divergir da tela do cliente.
  // Os `null` (sem contrato ainda) entram como zero, como na tela do cliente.
  const totais = linhas.reduce(
    (a, l) => ({
      totalSped: a.totalSped + Math.max(l.totalSped, 0),
      valueSped: a.valueSped + l.valueSped,
      total: a.total + (l.total ?? 0),
      valueBeneficio: a.valueBeneficio + (l.valueBeneficio ?? 0),
    }),
    { totalSped: 0, valueSped: 0, total: 0, valueBeneficio: 0 },
  );

  return (
    <div className="flex flex-col gap-4">
      {contribuinte === null ? (
        <Aviso tom="atencao" titulo="Não foi possível identificar o contribuinte">
          Nenhum arquivo trouxe o registro 0000 legível. Confira se são arquivos EFD completos.
        </Aviso>
      ) : (
        <Cartao
          como="section"
          titulo={contribuinte.razaoSocial}
          subtitulo="Identificado pelo arquivo"
        >
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">CNPJ</dt>
              <dd className="valor-fiscal">{formatarCnpj(contribuinte.cnpj)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">CAD/ICMS</dt>
              <dd className="valor-fiscal">{formatarCadIcms(contribuinte.cadIcms) || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">UF</dt>
              <dd>{contribuinte.uf || '—'}</dd>
            </div>
          </dl>
        </Cartao>
      )}

      {linhas.length > 0 && (
        <Tabela
          legenda={props.legenda ?? 'Cálculo por competência, antes do cadastro'}
          densidade="compacta"
          colunas={COLUNAS_PREVIA}
          dados={linhas}
          chaveDaLinha={(l) => l.competencia}
          chaveDeCabecalhoDaLinha="competencia"
          rodape={
            <tr>
              <CelulaDeTotal cabecalhoDaLinha>Total</CelulaDeTotal>
              <CelulaDeTotal numerica>
                <ValorFiscal valor={totais.totalSped} />
              </CelulaDeTotal>
              <CelulaDeTotal numerica>
                <span aria-hidden="true">—</span>
              </CelulaDeTotal>
              <CelulaDeTotal numerica>
                <ValorFiscal valor={totais.valueSped} />
              </CelulaDeTotal>
              <CelulaDeTotal numerica>
                <span aria-hidden="true">—</span>
              </CelulaDeTotal>
              <CelulaDeTotal numerica>
                <ValorFiscal valor={totais.total} />
              </CelulaDeTotal>
              <CelulaDeTotal numerica>
                <ValorFiscal valor={totais.valueBeneficio} variante="economia" />
              </CelulaDeTotal>
            </tr>
          }
        />
      )}

      {recusados.length > 0 && (
        <Aviso tom="atencao" titulo={`${recusados.length} arquivo(s) recusado(s)`}>
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-4">
            {recusados.map((r) => (
              <li key={r.arquivo}>
                <strong>{r.arquivo}:</strong> {r.motivo}
              </li>
            ))}
          </ul>
        </Aviso>
      )}

      {avisos.length > 0 && (
        <Aviso tom="informacao" titulo={`${avisos.length} aviso(s) de leitura`}>
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-4">
            {avisos.slice(0, 6).map((a, i) => (
              <li key={i}>
                <strong>{a.arquivo}:</strong> {a.mensagem}
              </li>
            ))}
          </ul>
        </Aviso>
      )}
    </div>
  );
}

const COLUNAS_PREVIA: readonly ColunaTabela<LinhaDaGrade>[] = [
  {
    chave: 'competencia',
    titulo: 'Mês',
    larguraMinima: '7rem',
    celula: (l) => formatarCompetencia(l.competencia),
  },
  {
    chave: 'totalSped',
    titulo: 'ICMS apurado',
    numerica: true,
    celula: (l) => <ValorFiscal valor={l.totalSped} />,
  },
  {
    chave: 'percentSpedPercentual',
    titulo: '% utilização',
    numerica: true,
    celula: (l) => <ValorPercentual percentual={l.percentSpedPercentual} />,
  },
  {
    chave: 'valueSped',
    titulo: 'Valor de utilização',
    numerica: true,
    celula: (l) => <ValorFiscal valor={l.valueSped} />,
  },
  {
    chave: 'percentBeneficioPercentual',
    titulo: '% deságio',
    numerica: true,
    celula: (l) =>
      l.percentBeneficioPercentual === null ? (
        <span aria-hidden="true">—</span>
      ) : (
        <ValorPercentual percentual={l.percentBeneficioPercentual} />
      ),
  },
  {
    chave: 'total',
    titulo: 'Valor a ser pago',
    numerica: true,
    celula: (l) =>
      l.total === null ? <span aria-hidden="true">—</span> : <ValorFiscal valor={l.total} />,
  },
  {
    chave: 'valueBeneficio',
    titulo: 'Ben. econômico',
    numerica: true,
    celula: (l) =>
      l.valueBeneficio === null ? (
        <span aria-hidden="true">—</span>
      ) : (
        <ValorFiscal valor={l.valueBeneficio} variante="economia" />
      ),
  },
];
